import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useTranslation } from "react-i18next";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { Button } from "@/components/Button/Button";

import { InputField } from "@/components/InputField/InputField";

import { ButtonColor } from "@/components/Button/types";

import { Table } from "@/components/Table/Table";

import { Modal } from "@/components";

import {
	useCreateSwarmMutation,
	useUpdateSwarmMutation,
	usePreviewSwarmMutation,
} from "@/pages/Private/redux/swarm/swarm.api";

import {
	MultiAutoCompleteApi,
	MultiAutoCompleteApiItem,
} from "@/components/MultiAutoCompleteApi/MultiAutoCompleteApi";

import { Toggle } from "@/components/Toggle/Toggle";

import { CreateSwarm, CreateSwarmSchema, Swarm } from "../schema/swarm";

import { List } from "../../List/schema/list";

import { swarmPreviewColumns } from "./swarmPreviewColumns";

interface CreateSwarmFormProps {
	swarm?: Swarm | null;
	list: List;
	handleSelect?: (swarm: Swarm | null) => void;
	handleDuplicate?: (swarm: Swarm | null) => void;
}

export const CreateSwarmForm: FunctionComponent<CreateSwarmFormProps> = ({
	swarm,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`swarm.${key}`);

	const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

	const [createSwarm, { error, isLoading }] = useCreateSwarmMutation();
	const [updateSwarm, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateSwarmMutation();
	const [
		previewSwarm,
		{ isLoading: previewIsLoading, data: previewSwarmData, error: previewError },
	] = usePreviewSwarmMutation();

	const [showExcludeCompanyNames, setShowExcludeCompanyNames] = useState<boolean>(false);
	const [showExcludeJobTitles, setShowExcludeJobTitles] = useState<boolean>(false);

	// A failed preview is shown with the form's errors below, never as "0 results" (PREC-4462).
	const handlePreviewJob = async () => {
		const result = await previewSwarm({
			listId: list.id,
			name: formValues.name || "Preview",
			jobTitles: formValues.jobTitles,
			excludeJobTitles: formValues.excludeJobTitles,
			jobLevels: formValues.jobLevels,
			countries: formValues.countries,
			cities: formValues.cities,
			states: formValues.states,
			companyNames: formValues.companyNames,
			excludeCompanyNames: formValues.excludeCompanyNames,
			companySizes: formValues.companySizes,
			companyIndustries: formValues.companyIndustries,
			companyWebsites: formValues.companyWebsites,
			companyCountries: formValues.companyCountries,
			companyStates: formValues.companyStates,
			companyCities: formValues.companyCities,
			schoolNames: formValues.schoolNames,
			pastEmployers: formValues.pastEmployers,
			yearsInCurrentRole: formValues.yearsInCurrentRole,
			linkedinSlugs: null,
		})
			.unwrap()
			.catch(() => null);

		if (result?.data) {
			setShowPreviewModal(true);
		}
	};

	const initialState: Partial<CreateSwarm> = {
		name: "",
		jobTitles: [],
		excludeJobTitles: [],
		jobLevels: [],
		countries: [],
		cities: [],
		states: [],
		companyNames: [],
		excludeCompanyNames: [],
		companySizes: [],
		companyIndustries: [],
		companyWebsites: [],
		companyCountries: [],
		companyStates: [],
		companyCities: [],
		schoolNames: [],
		pastEmployers: [],
		listId: list.id,
		yearsInCurrentRole: null,
	};

	const {
		reset,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CreateSwarm>({
		defaultValues: initialState,
		resolver: zodResolver(CreateSwarmSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (swarm) {
			if (swarm.excludeJobTitles && swarm.excludeJobTitles.length > 0) {
				setShowExcludeJobTitles(true);
			}

			if (swarm.excludeCompanyNames && swarm.excludeCompanyNames.length > 0) {
				setShowExcludeCompanyNames(true);
			} else {
				setShowExcludeCompanyNames(false);
			}

			const formState = {
				name: swarm.name,
				jobTitles: swarm.jobTitles,
				excludeJobTitles: swarm.excludeJobTitles,
				jobLevels: swarm.jobLevels,
				countries: swarm.countries,
				cities: swarm.cities,
				states: swarm.states,
				companyNames: swarm.companyNames,
				excludeCompanyNames: swarm.excludeCompanyNames,
				companySizes: swarm.companySizes,
				companyIndustries: swarm.companyIndustries,
				companyWebsites: swarm.companyWebsites,
				companyCountries: swarm.companyCountries,
				companyStates: swarm.companyStates,
				companyCities: swarm.companyCities,
				schoolNames: swarm.schoolNames,
				pastEmployers: swarm.pastEmployers,
				yearsInCurrentRole: swarm.yearsInCurrentRole,
				listId: list.id,
			};

			if (handleDuplicate) {
				reset({
					...formState,
				});
			} else {
				reset({
					id: swarm.id,
					...formState,
				});
			}
		} else {
			reset({ ...initialState, listId: list.id });
		}
	}, [swarm, list.id]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	console.log(errors);

	const onSubmit = async (values: CreateSwarm) => {
		try {
			if (values.id) {
				await updateSwarm({ id: values.id, ...values }).unwrap();
			} else {
				await createSwarm({ ...values, listId: list.id }).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);

			reset();
		} catch (err) {
			console.error(err);
		}
	};
	const buttonTitle =
		handleDuplicate && swarm?.id
			? t("basics.copyCriteria")
			: swarm?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<>
			<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
				<div className="text-md mb-5">Add a source</div>

				<div className="flex flex-row gap-4 w-full mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue("name", event.target.value, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("sourceNameLabel")}
						name={"name"}
						placeholder={ts("namePlaceholder")}
						value={formValues.name ?? ""}
					/>
				</div>
				<div className="text-sm font-semibold mb-5">Person related filters</div>
				<div className="flex flex-col gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("jobTitles", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("jobTitlesLabel")}
						placeholder={ts("jobTitlesPlaceholder")}
						propertyName="title"
						selectedItems={
							formValues.jobTitles?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-col gap-4 w-full mb-4">
					<div className="flex flex-row gap-2 items-center">
						<Toggle
							isChecked={showExcludeJobTitles}
							onChange={() => {
								setShowExcludeJobTitles(!showExcludeJobTitles);
								setValue("excludeJobTitles", [], {
									shouldDirty: true,
									shouldValidate: true,
								});
							}}
						/>
						<div className="text-sm font-medium">{ts("excludeJobTitlesLabel")}</div>
					</div>
					<MultiAutoCompleteApi
						disabled={!showExcludeJobTitles}
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("excludeJobTitles", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("excludeJobTitlesLabel")}
						placeholder={ts("excludeJobTitlesPlaceholder")}
						propertyName="title"
						selectedItems={
							formValues.excludeJobTitles?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("jobLevels", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("jobLevelsLabel")}
						placeholder={ts("jobLevelsPlaceholder")}
						preload={true}
						propertyName="swarmSeniority"
						selectedItems={
							formValues.jobLevels?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("countries", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("countriesLabel")}
						placeholder={ts("countriesPlaceholder")}
						propertyName="country"
						selectedItems={
							formValues.countries?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>
				{/* 
				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("states", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("statesLabel")}
						placeholder={ts("statesPlaceholder")}
						propertyName="state"
						selectedItems={
							formValues.states?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div> */}

				{/* <div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("cities", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("citiesLabel")}
						placeholder={ts("citiesPlaceholder")}
						propertyName="city"
						selectedItems={
							formValues.cities?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div> */}

				<div className="text-sm font-semibold my-5">Company related filters</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("companyNames", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyNamesLabel")}
						noData={true}
						placeholder={ts("companyNamesPlaceholder")}
						propertyName="company_name"
						selectedItems={
							formValues.companyNames?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-col gap-4 w-full mb-4">
					<div className="flex flex-row gap-2 items-center">
						<Toggle
							isChecked={showExcludeCompanyNames}
							onChange={() => {
								setShowExcludeCompanyNames(!showExcludeCompanyNames);
								setValue("excludeCompanyNames", [], {
									shouldDirty: true,
									shouldValidate: true,
								});
							}}
						/>
						<div className="text-sm font-medium">{ts("allowExcludeCompanyNamesLabel")}</div>
					</div>
					<MultiAutoCompleteApi
						disabled={!showExcludeCompanyNames}
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("excludeCompanyNames", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("excludeCompanyNamesLabel")}
						noData={true}
						placeholder={ts("excludeCompanyNamesPlaceholder")}
						propertyName="company_name"
						selectedItems={
							formValues.excludeCompanyNames?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("companyWebsites", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyWebsitesLabel")}
						noData={true}
						placeholder={ts("companyWebsitesPlaceholder")}
						propertyName="website"
						selectedItems={
							formValues.companyWebsites?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("companyIndustries", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyIndustriesLabel")}
						placeholder={ts("companyIndustriesPlaceholder")}
						propertyName="industry"
						selectedItems={
							formValues.companyIndustries?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("companySizes", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companySizesLabel")}
						placeholder={ts("companySizesPlaceholder")}
						preload={true}
						propertyName="reportedEmployees"
						selectedItems={
							formValues.companySizes?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("companyCountries", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyCountriesLabel")}
						placeholder={ts("companyCountriesPlaceholder")}
						propertyName="country"
						selectedItems={
							formValues.companyCountries?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="text-sm font-semibold my-5">Additional search criteria</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("schoolNames", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("schoolNamesLabel")}
						placeholder={ts("schoolNamesPlaceholder")}
						propertyName="school"
						selectedItems={
							formValues.schoolNames?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4">
					<MultiAutoCompleteApi
						handleSelect={function (value?: MultiAutoCompleteApiItem[]): void {
							setValue("pastEmployers", value?.map((r) => r.id.toString()) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("pastEmployersLabel")}
						noData={true}
						placeholder={ts("pastEmployersPlaceholder")}
						propertyName="past_employer"
						selectedItems={
							formValues.pastEmployers?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
					/>
				</div>

				<div className="flex flex-row gap-4 w-full mb-4 text-sm font-medium">
					All profiles will be pulled from various lead databases instantanously. Lead databases
					refresh their data on a regular basis. However, some results might be outdated.
				</div>
				<div className="flex flex-row gap-4 w-full mb-4 text-sm font-medium">
					Signalist verifies every returned profile in real-time to make sure that the person still
					has the current role at the company and all data is correct
				</div>

				{getAllErrors(error || updateError || previewError, formErrors).length ? (
					<div className="mb-4">
						{renderErrorMessages(getAllErrors(error || updateError || previewError, formErrors))}
					</div>
				) : null}

				<div className="flex gap-4 w-full justify-end">
					{handleSelect && (
						<div className="flex max-w-[100px] w-full">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title="cancel"
								onClick={() => {
									handleSelect(null);
									handleDuplicate?.(null);
									reset(initialState);
								}}
							/>
						</div>
					)}
					<div className="flex w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={
								!formValues.jobTitles?.length &&
								!formValues.excludeJobTitles?.length &&
								!formValues.jobLevels?.length &&
								!formValues.countries?.length &&
								!formValues.cities?.length &&
								!formValues.states?.length &&
								!formValues.companyNames?.length &&
								!formValues.excludeCompanyNames?.length &&
								!formValues.companySizes?.length &&
								!formValues.companyIndustries?.length &&
								!formValues.companyWebsites?.length &&
								!formValues.companyCountries?.length &&
								!formValues.companyStates?.length &&
								!formValues.companyCities?.length &&
								!formValues.schoolNames?.length &&
								!formValues.pastEmployers?.length &&
								!formValues.yearsInCurrentRole
							}
							isLoading={previewIsLoading}
							title="Preview"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();

								handlePreviewJob();
							}}
						/>
					</div>
					<div className="flex max-w-[150px] w-full">
						<Button
							isLoading={isLoading || updateIsLoading}
							title={buttonTitle}
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</div>
			</div>
			{showPreviewModal && (
				<Modal
					handleClose={() => setShowPreviewModal(false)}
					isOpened={showPreviewModal}
					size="lg"
					title="Preview"
				>
					<Table
						columns={swarmPreviewColumns}
						data={previewSwarmData?.data || []}
						hideFooter={true}
						hidePagination={true}
					/>
					{previewSwarmData?.totalCount && previewSwarmData?.totalCount > 0 && (
						<div className="flex items-center justify-center">
							There are {previewSwarmData?.totalCount} profiles found.
						</div>
					)}
					{previewSwarmData?.totalCount && previewSwarmData?.totalCount === 0 && (
						<div className="flex items-center justify-center">There are no profiles found.</div>
					)}
				</Modal>
			)}
		</>
	);
};
